// Client-view viewer block (map + 3D + floating panel), fully read-only.
// REUSES the console's real Viewer / Model3DView / DefectList / SourceGallery
// (../app/defects/*) — the client sees the exact same inspection canvas the
// provider does, minus every edit affordance. Driven entirely by the snapshot.
import { useEffect, useMemo, useRef, useState } from "react";
import type { ActiveOrtho } from "../app/defects/geo";
import { IcCube, IcEye, IcEyeOff, IcImage, IcLayers, IcList, IcX } from "../app/defects/icons";
import { DefectList } from "../app/defects/list";
import { catOf, sevMeta } from "../app/defects/meta";
import { Model3DView } from "../app/defects/model3d";
import { SourceGallery } from "../app/defects/sources";
import type { Defect, SourceImage } from "../app/defects/types";
import { Viewer, ViewSwitcher, type ViewDef } from "../app/defects/viewer";
import { PhotoDock } from "../app/defects/workspace";
import { fmtR, remTotal, type RemItem } from "../app/remediation/data";
import { RemMiniRow, popoverRemExtra } from "./remediation";
import { orthoToActive, type SnapshotModel } from "./snapshot";
import { SvMeta } from "./ui";

// Ad hoc photo surfaces sit in the view state as "photo:<id>" (mirrors the
// console workspace); a defect's `view` field holds the bare surface id.
const PHOTO_PREFIX = "photo:";
const asPhotoView = (id: string) => PHOTO_PREFIX + id;
const photoIdOf = (v: string) => (v.startsWith(PHOTO_PREFIX) ? v.slice(PHOTO_PREFIX.length) : null);

function sortDefects(defects: Defect[], search: string, filterSev: string, filterCat: string): Defect[] {
  const q = (search || "").trim().toLowerCase();
  return defects
    .filter((d) =>
      (filterSev === "all" || d.severity === filterSev) &&
      (filterCat === "all" || catOf(d) === filterCat) &&
      (!q || d.title.toLowerCase().includes(q) || (d.desc || "").toLowerCase().includes(q) || ("#" + d.ref).includes(q)))
    .sort((a, b) => sevMeta(b.severity).rank - sevMeta(a.severity).rank || a.ref - b.ref);
}

function SvPanelTab({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ flex: 1, padding: "11px 4px 10px", border: "none", borderBottom: `2px solid ${on ? "var(--di-orange)" : "transparent"}`, background: h && !on ? "rgba(255,255,255,0.03)" : "transparent", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: on ? "var(--di-orange)" : "var(--di-muted)", transition: "all 150ms ease" }}>
      {label}
    </button>);
}

export function ClientViewer({ model, panelDefault, onOpenRem }: {
  model: SnapshotModel;
  panelDefault: boolean;
  onOpenRem: (it: RemItem) => void;
}) {
  const { snapshot, defects, photos, sourcesById, remItems } = model;
  const hasRem = remItems.length > 0;

  // Three-item switcher: orthos (dropdown), 3D, and ad hoc photos — same as the
  // console. Photos appear only when the share included them (photos non-empty).
  const wsViews: ViewDef[] = useMemo(() => [
    ...snapshot.orthos.map((o) => ({ id: o.orthoKey, label: o.label, Ic: o.view === "top" ? IcLayers : IcImage, available: true })),
    ...(snapshot.model3d ? [{ id: "model", label: "3D", Ic: IcCube, available: true }] : []),
  ], [snapshot]);
  const switcherCount = wsViews.length + (photos.length > 0 ? 1 : 0);
  const firstView = wsViews[0]?.id ?? (photos.length ? asPhotoView(photos[0]!.id) : "model");

  const [view, setView] = useState<string>(firstView);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ id: string | null; seq: number }>({ id: null, seq: 0 });
  const [search, setSearch] = useState("");
  const [filterSev, setFilterSev] = useState<string>("all");
  const [filterCat, setFilterCat] = useState<string>("all");
  const [panelOpen, setPanelOpen] = useState(panelDefault !== false);
  const [hideDefects, setHideDefects] = useState(false);
  const [tab, setTab] = useState<"defects" | "remediation">("defects");
  const [gallery, setGallery] = useState<{ defect: Defect; index: number } | null>(null);
  useEffect(() => { setPanelOpen(panelDefault !== false); }, [panelDefault]);

  const photoView = photoIdOf(view);
  const isPhoto = photoView != null;
  const photo = isPhoto ? photos.find((p) => p.id === photoView) ?? null : null;
  const activeOrtho = snapshot.orthos.find((o) => o.orthoKey === view) ?? null;
  const lastPhoto = useRef<string | null>(null);
  useEffect(() => { if (isPhoto) lastPhoto.current = view; }, [view, isPhoto]);
  // Photo surface removed while viewing it (or none): fall back to the first view.
  useEffect(() => { if (isPhoto && !photo) setView(firstView); }, [isPhoto, photo, firstView]);
  const openPhotos = () => {
    if (isPhoto || !photos.length) return;
    const lp = lastPhoto.current;
    setView(lp && photos.some((p) => asPhotoView(p.id) === lp) ? lp : asPhotoView(photos[0]!.id));
  };

  // §7 #5: a defect belongs to ONE surface — scope pins/list/counts to the
  // active surface: the current ad hoc photo, or the current ortho.
  const scoped = useMemo(() => {
    if (photoView) return defects.filter((d) => d.view === photoView);
    if (activeOrtho) return defects.filter((d) => !d.view && d.orthoViewId === activeOrtho.orthoKey);
    return [];
  }, [defects, photoView, activeOrtho]);

  // The surface fed to the Viewer: an ad hoc photo renders as a flat CRS.Simple
  // image plane (photoSrc, no georeference); orthos render their tile pyramid.
  const activeSurface: ActiveOrtho | null = photo
    ? { id: photo.id, tileUrl: "", width: photo.width, height: photo.height, maxZoom: 0, tileSize: 256, gsdM: null, photoSrc: photo.src }
    : activeOrtho ? orthoToActive(activeOrtho) : null;

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: scoped.length };
    (["major", "moderate", "minor"] as const).forEach((s) => { c[s] = scoped.filter((d) => d.severity === s).length; });
    return c;
  }, [scoped]);
  const catCounts = useMemo(() => {
    const c: Record<string, number> = { all: scoped.length };
    scoped.forEach((d) => { const k = catOf(d); c[k] = (c[k] ?? 0) + 1; });
    return c;
  }, [scoped]);
  const filtered = useMemo(() => sortDefects(scoped, search, filterSev, filterCat), [scoped, search, filterSev, filterCat]);

  const resolveSources = (d: Defect): SourceImage[] => sourcesById.get(d.id) ?? [];
  const focusDefect = (id: string) => {
    const d = defects.find((x) => x.id === id);
    if (d) {
      const target = d.view
        ? asPhotoView(d.view)
        : d.orthoViewId && snapshot.orthos.some((o) => o.orthoKey === d.orthoViewId)
          ? d.orthoViewId
          : view === "model" && snapshot.orthos.length ? snapshot.orthos[0]!.orthoKey : view;
      if (target !== view && target !== "model") setView(target);
    }
    setSelectedId(id);
    setFocus((f) => ({ id, seq: f.seq + 1 }));
  };

  const remSorted = useMemo(() => [...remItems].sort((a, b) => remTotal(b) - remTotal(a)), [remItems]);
  const remTotalAll = remItems.reduce((a, it) => a + remTotal(it), 0);
  const defMap = useMemo(() => { const m: Record<string, Defect> = {}; defects.forEach((d) => { m[d.id] = d; }); return m; }, [defects]);
  const popExtra = hasRem ? popoverRemExtra(remItems, onOpenRem) : null;

  const tabs = [{ id: "defects" as const, label: "Defects" }, ...(hasRem ? [{ id: "remediation" as const, label: "Remediation" }] : [])];

  return (
    <div style={{ position: "absolute", inset: 0, background: "var(--di-void)", overflow: "hidden" }}>
      {view === "model" && snapshot.model3d
        ? <Model3DView url={snapshot.model3d.url} />
        : activeSurface
          ? <Viewer key={view} ortho={activeSurface} view={view} defects={scoped}
              selectedId={selectedId} tool="select" movingId={null} filterSev={filterSev} filterCat={filterCat}
              focus={focus} readOnly hideDefects={hideDefects} popoverExtra={popExtra} resolveSources={resolveSources}
              onSelect={(id) => setSelectedId(id)}
              onViewSources={(d, i = 0) => setGallery({ defect: d, index: i })}>
              {isPhoto && photo &&
                <PhotoDock photos={photos} active={photo} defects={defects} onPick={(id) => setView(asPhotoView(id))} readOnly />}
            </Viewer>
          : null}

      {switcherCount > 1 &&
        <div style={{ position: "absolute", top: 14, left: 14, zIndex: 50, background: "rgba(14,18,25,0.86)", border: "1px solid var(--di-border)", borderRadius: 12, padding: 4, backdropFilter: "blur(8px)" }}>
          <ViewSwitcher view={view} setView={setView} defectCounts={scoped.length} views={wsViews}
            photos={{ count: photos.length, active: isPhoto, onClick: openPhotos }} />
        </div>}

      <div style={{ position: "absolute", top: 14, right: 14, zIndex: 50, display: "flex", gap: 8 }}>
        <button onClick={() => setHideDefects((h) => !h)} title={hideDefects ? "Show defects" : "Hide defects"}
          style={{ width: 36, height: 34, borderRadius: 9, border: "1px solid var(--di-border)", background: hideDefects ? "var(--di-orange)" : "rgba(14,18,25,0.86)", color: hideDefects ? "#160d03" : "var(--di-muted-2)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(8px)" }}>
          {hideDefects ? <IcEyeOff size={15} color="#160d03" /> : <IcEye size={15} color="var(--di-muted-2)" />}
        </button>
        <button onClick={() => setPanelOpen((o) => !o)} title={panelOpen ? "Hide panel" : "Show panel"}
          style={{ width: 36, height: 34, borderRadius: 9, border: "1px solid var(--di-border)", background: panelOpen ? "var(--di-orange)" : "rgba(14,18,25,0.86)", color: panelOpen ? "#160d03" : "var(--di-muted-2)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(8px)" }}>
          <IcList size={15} color={panelOpen ? "#160d03" : "var(--di-muted-2)"} />
        </button>
      </div>

      {panelOpen &&
        <div style={{ position: "absolute", top: 66, right: 14, bottom: 14, width: 330, zIndex: 45, background: "rgba(11,15,22,0.92)", border: "1px solid var(--di-border)", borderRadius: 16, backdropFilter: "blur(14px)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {tabs.length > 1 &&
            <div style={{ display: "flex", borderBottom: "1px solid var(--di-border)", flexShrink: 0 }}>
              {tabs.map((tb) => <SvPanelTab key={tb.id} label={tb.label} on={tab === tb.id} onClick={() => setTab(tb.id)} />)}
            </div>}

          {tab === "defects" &&
            <DefectList defects={filtered} selectedId={selectedId} onSelect={focusDefect} search={search} setSearch={setSearch}
              filterSev={filterSev as never} setFilterSev={(v) => setFilterSev(v)} counts={counts}
              filterCat={filterCat as never} setFilterCat={(v) => setFilterCat(v)} catCounts={catCounts} compact
              headerRight={<button onClick={() => setPanelOpen(false)} style={{ width: 26, height: 26, borderRadius: 7, border: "1px solid var(--di-border)", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--di-subtle)" }}><IcX size={13} /></button>} />}

          {tab === "remediation" && hasRem &&
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <div style={{ padding: "14px 14px 12px", borderBottom: "1px solid var(--di-border)", display: "flex", alignItems: "baseline", gap: 8, flexShrink: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.01em" }}>Remediation plan</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: "var(--di-orange)" }}>{remSorted.length}</span>
              </div>
              <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                {remSorted.map((it, i) => <RemMiniRow key={it.id} it={it} defMap={defMap} last={i === remSorted.length - 1} onView={onOpenRem} onLocate={snapshot.orthos.length ? focusDefect : null} />)}
              </div>
              <div style={{ padding: "11px 14px", borderTop: "1px solid var(--di-border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <SvMeta color="var(--di-label)">Combined estimate</SvMeta>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--di-orange)" }}>{fmtR(remTotalAll)}</span>
              </div>
            </div>}
        </div>}

      {gallery &&
        <SourceGallery defect={gallery.defect} sources={resolveSources(gallery.defect)} bin={[]} catalog={[]} readOnly
          index={gallery.index} setIndex={(i) => setGallery((g) => (g ? { ...g, index: i } : g))}
          onClose={() => setGallery(null)} />}
    </div>);
}
